'use client';

import React from 'react';
import Tooltip from '@/ui/Tooltip';
import { parseAndSplitByH2 } from '@/lib/parseMarkdown';
import { normalizeLinkTooltipUrl } from '@/lib/linkTooltipUrls';
import Markdown from '../Markdown';
import { LINK_TOOLTIP_MARKDOWN } from '@/content/link-tooltips/_generated';

// Hover tooltips for specific external links: any markdown link whose URL is
// in LINK_TOOLTIP_MARKDOWN gets a hover card rendering that markdown, with
// the destination URL in small type at the bottom. The map is generated from
// the "Link Previews" tab of the Plan A Google Doc by the import pipeline
// (scripts/planA/linkPreviews.ts): a link-only line in that tab starts a
// preview, and the content under it is the tooltip markdown. Links are
// flagged by URL rather than by any in-text marker so the tooltips survive
// gdoc content re-imports without doc edits — same idea as
// IMAGE_FIGURE_REPLACEMENTS in Markdown.tsx.

export function getLinkTooltipMarkdown(url: string): string | undefined {
  return LINK_TOOLTIP_MARKDOWN[normalizeLinkTooltipUrl(url)];
}

// Shared by a preview link's anchor and its tooltip content so dwell
// on the open card still counts (the tracker dedupes by id). Fragment-
// only URLs normalize to '' — fall back to the raw URL.
export function linkPreviewHoverId(url: string): string {
  return `link-preview:${normalizeLinkTooltipUrl(url) || url}`;
}

// The card only mounts while the tooltip is open, so the markdown parse never
// runs for links that are never hovered.
function LinkTooltipCard({ markdown, url }: { markdown: string; url: string }) {
  const chunks = React.useMemo(() => parseAndSplitByH2(markdown), [markdown]);
  const displayUrl = normalizeLinkTooltipUrl(url).replace(/^https?:\/\//, '');
  return (
    // flex-col + min-h-0/shrink-0 so that when the tooltip's size() middleware
    // clamps the floating element (small/mobile viewports), the markdown pane
    // is what shrinks and scrolls — keeping its full content reachable and the
    // URL footer pinned visible — instead of overflow-hidden clipping the
    // card's bottom.
    <div
      data-track-hover={linkPreviewHoverId(url)}
      className="w-[420px] max-w-full min-h-0 flex flex-col bg-vivid-background border-[1.5px] border-black rounded-md overflow-hidden"
    >
      <div className="markdown-content min-h-0 max-h-[60vh] overflow-y-auto px-4 pt-3 pb-0 font-[et-book,Palatino,'Palatino_Linotype','Book_Antiqua',Georgia,serif] text-black text-[1.1rem] leading-[1.45] [&_p]:text-[1.1rem] [&_p]:leading-[1.45] [&_p]:my-[0.5em] [&>*>p:first-child]:mt-0 [&>*>p:last-child]:mb-0 [&>div.my-6]:!mt-3 [&>div.my-6]:!mb-0">
        {chunks.map((chunk, i) => (
          <Markdown key={i} nodes={chunk.nodes} docContext={chunk.docContext} />
        ))}
      </div>
      <div className="shrink-0 px-4 pb-2.5 font-mono text-[11px] lineclamp leading-snug text-black/50 break-all text-ellipsis overflow-hidden">
        <span className="truncate">{displayUrl}</span>
      </div>
    </div>
  );
}

export function LinkTooltip({ url, markdown, children }: {
  url: string;
  markdown: string;
  children: React.ReactElement;
}) {
  return (
    <Tooltip
      content={<LinkTooltipCard markdown={markdown} url={url} />}
      placement="bottom-start"
      interactive
      maxWidth={440}
      disableShadow
    >
      {children}
    </Tooltip>
  );
}
